import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import locationService from '../../services/LocationService.js';
import { INDIAN_STATES } from './indianStates.js';
import { MapPin, ArrowLeft } from "lucide-react";

const EditLocation = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [stateSearch, setStateSearch] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [formData, setFormData] = useState({
    city: '',
    state: '',
    city_code: ''
  });

  useEffect(() => {
    fetchLocation();
  }, [locationId]);

  const filteredStates = INDIAN_STATES.filter(state =>
    state.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const fetchLocation = async () => {
    try {
      setLoading(true);
      const response = await locationService.getById(locationId);
      const location = response.data;
      
      if (location) {
        setLocationData(location);
        setFormData({
          city: location.city || '',
          state: location.state || '',
          city_code: location.city_code || ''
        });
      } else {
        Alert.error('Location not found');
      }
    } catch (err) {
      console.error('Error fetching location:', err);
      Alert.error(err.response?.data?.error || 'Failed to load location');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.city.trim()) {
      Alert.error('City is required');
      return false;
    }
    if (!formData.state) {
      Alert.error('State is required');
      return false;
    }
    if (!formData.city_code.trim()) {
      Alert.error('City code is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const confirmed = await Alert.confirm('Are you sure you want to update this location?', 'Update Location');
    if (!confirmed) return;

    try {
      setUpdating(true);
      
      const submitData = {
        city: formData.city.trim(),
        state: formData.state,
        city_code: formData.city_code.trim().toUpperCase()
      };

      await locationService.update(locationId, submitData);
      
      Alert.success('Location updated successfully!');
      navigate('/locations');
      
    } catch (err) {
      console.error('Error updating location:', err);
      Alert.error(err.response?.data?.error || 'Failed to update location');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 flex items-center justify-center pt-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading location...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!locationData) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 flex items-center justify-center pt-20 p-4">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md text-center">
              <div className="text-6xl mb-4">❌</div>
              <p className="text-red-600 text-lg font-bold mb-2">Location Not Found</p>
              <p className="text-gray-600 mb-4">The location you are looking for does not exist.</p>
              <button
                onClick={() => navigate('/locations')}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition"
              >
                Back to Locations
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* LEFT TITLE */}
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <MapPin className="w-6 h-6 text-buttonBg" />
                      Edit Location
                    </h1>

                    {/* RIGHT BUTTON */}
                    <button
                      onClick={() => navigate('/locations')}
                      className="
                                                  relative overflow-hidden
                                                  group flex items-center justify-center gap-2
                                                  px-3 sm:px-3 py-2.5 sm:py-2
                                                  text-xs sm:text-sm
                                                  rounded-lg
                                                  border border-buttonBg
                                                  bg-buttonBg text-buttonTxt
                                                  font-medium whitespace-nowrap
                      
                                                  transition-colors duration-300 ease-out
                                                  hover:bg-transparent hover:text-buttonBg
                                                "
                                              >
                                                {/* COLLAPSING BACKGROUND */}
                                                <span
                                                  className="
                                                    absolute inset-0
                                                    bg-buttonBg
                                                    scale-x-100
                                                    group-hover:scale-x-0
                                                    origin-center
                                                    transition-transform duration-300 ease-out
                                                    z-0
                                                  "
                                                />
                      
                                                {/* ICON */}
                                                <ArrowLeft
                                                  className="
                                                    w-4 h-4 relative z-10
                                                    transition-transform duration-300 ease-out
                                                    group-hover:scale-90
                                                  "
                                                />
                      
                                                <span className="relative z-10">Back to Location List</span>
                    </button>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-6 space-y-6 sm:space-y-8">
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Location Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                    {/* City */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Enter city name"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        required
                        disabled={updating}
                      />
                    </div>

                    {/* State */}
                    <div className="relative">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        State <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.state || stateSearch}
                        onChange={(e) => {
                          setStateSearch(e.target.value);
                          setFormData({ ...formData, state: '' });
                          setShowStateDropdown(true);
                        }}
                        onFocus={() => setShowStateDropdown(true)}
                        placeholder="Search and select state"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        required
                        disabled={updating}
                        autoComplete="off"
                      />
                      {showStateDropdown && filteredStates.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-sm shadow-lg max-h-60 overflow-y-auto">
                          {filteredStates.map((state) => (
                            <div
                              key={state}
                              onClick={() => {
                                setFormData({ ...formData, state: state });
                                setStateSearch('');
                                setShowStateDropdown(false);
                              }}
                              className="px-3 sm:px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm sm:text-base"
                            >
                              {state}
                            </div>
                          ))}
                        </div>
                      )}
                      {formData.state && (
                        <div className="mt-2 text-xs sm:text-sm text-gray-600">
                          Selected: <span className="font-medium">{formData.state}</span>
                        </div>
                      )}
                    </div>

                    {/* City Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        City Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="city_code"
                        value={formData.city_code}
                        onChange={handleChange}
                        placeholder="e.g., MUM for Mumbai"
                        maxLength="10"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base uppercase"
                        required
                        disabled={updating}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Warning: Changing city code will update all related survey and cleaning IDs
                      </p>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      disabled={updating}
                      className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          <span className="relative z-10">
                      {updating ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </>
                      ) : (
                        <>
                          Update Location
                        </>
                      )}
                    </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/locations')}
                      disabled={updating}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					  {/* ICON 
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLocation;